import React, { useState, useEffect } from 'react';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import { settingsAPI } from '../services/api';
import { getLocation } from '../utils/locationUtils';
import PhoneInput, { isValidPhoneNumber, getCountries, getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import Input from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';
import { CURRENCY_LABELS, COUNTRY_CURRENCY_MAP } from '../utils/currencyMap';
import './Settings.css';

const Settings = () => {
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    mobile: '',
    role: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [orgData, setOrgData] = useState({
    name: '',
    type: '',
    country: '',
    timezone: '',
    baseCurrency: 'INR'
  });
  const [notifications, setNotifications] = useState({
    taskAssigned: true,
    dependencyCleared: true,
    deadlineApproaching: true,
    milestoneApproved: true,
    emailNotifications: false
  });
  const [attendanceSettings, setAttendanceSettings] = useState({
    standardPunchInTime: '09:00',
    lateThresholdMinutes: 30,
    maxLateAllowed: 3,
    resetPeriod: 'Monthly',
    officeCoordinates: {
      latitude: '',
      longitude: ''
    },
    officeRadius: 200
  });
  const [selectedMobileCountry, setSelectedMobileCountry] = useState('IN');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    loadSettings();
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      if (activeTab === 'profile') {
        const res = await settingsAPI.getProfile();
        setProfileData({
          name: res.data.data.name,
          email: res.data.data.email,
          mobile: res.data.data.mobile,
          role: res.data.data.role
        });
        if (res.data.data.mobile) {
          try {
            const parsed = parsePhoneNumber(res.data.data.mobile);
            if (parsed) {
              setSelectedMobileCountry(parsed.country);
            }
          } catch (e) {
            console.warn('Failed to parse mobile number:', res.data.data.mobile);
          }
        }
      } else if (activeTab === 'organization') {
        const res = await settingsAPI.getOrganization();
        setOrgData(res.data.data);
      } else if (activeTab === 'notifications') {
        const res = await settingsAPI.getNotifications();
        setNotifications(res.data.data);
      } else if (activeTab === 'attendance') {
        const res = await settingsAPI.getAttendanceSettings();
        setAttendanceSettings({
          ...res.data.data,
          officeCoordinates: res.data.data.officeCoordinates || { latitude: '', longitude: '' },
          officeRadius: res.data.data.officeRadius || 200
        });
      }
    } catch (error) {
      console.error('Load settings error:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    if (profileData.mobile && !isValidPhoneNumber(profileData.mobile)) {
      setPhoneError('Please enter a valid mobile number for the selected country');
      return;
    }

    setLoading(true);
    try {
      const res = await settingsAPI.updateProfile({
        name: profileData.name,
        mobile: profileData.mobile
      });
      setUser({ ...user, name: profileData.name, mobile: profileData.mobile });
      toast.success(res.data.message);
      setPhoneError('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage('error', 'Passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await settingsAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      showMessage('success', res.data.message);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleOrgUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await settingsAPI.updateOrganization({
        name: orgData.name,
        country: orgData.country,
        timezone: orgData.timezone,
        baseCurrency: orgData.baseCurrency
      });
      showMessage('success', res.data.message);
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationToggle = async (key) => {
    const updated = { ...notifications, [key]: !notifications[key] };
    setNotifications(updated);
    try {
      await settingsAPI.updateNotifications(updated);
    } catch (error) {
      setNotifications(notifications);
      showMessage('error', 'Failed to update notification preferences');
    }
  };

  const handleAttendanceSettingsUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await settingsAPI.updateAttendanceSettings(attendanceSettings);
      showMessage('success', 'Attendance settings updated successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to update attendance settings');
    } finally {
      setLoading(false);
    }
  };

  const captureOfficeLocation = async () => {
    setLoading(true);
    try {
      const coords = await getLocation();
      if (coords) {
        setAttendanceSettings({
          ...attendanceSettings,
          officeCoordinates: {
            latitude: coords.latitude,
            longitude: coords.longitude
          }
        });
        showMessage('success', 'Current location captured! Click "Save Attendance Rules" to apply.');
      } else {
        showMessage('error', 'Could not capture location. Please check permissions.');
      }
    } catch (error) {
      showMessage('error', 'Failed to capture location');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="settings-page">
        <div className="settings-header-minimal">
          <div className="header-info">
            <h1 className="page-title">Settings</h1>
            <p className="page-subtitle">Configure your account and team preferences</p>
          </div>
        </div>

        {message.text && (
          <div className={`marsh-alert alert-${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Marshmallow Tabbed Navigation */}
        <div className="settings-tabs-container">
          <button
            className={`marsh-tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`marsh-tab-btn ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            Password
          </button>
          {(user?.role === 'HR' || user?.role === 'Operations Head') && (
            <button
              className={`marsh-tab-btn ${activeTab === 'organization' ? 'active' : ''}`}
              onClick={() => setActiveTab('organization')}
            >
              Organization
            </button>
          )}
          {user?.role === 'HR' && (
            <button
              className={`marsh-tab-btn ${activeTab === 'attendance' ? 'active' : ''}`}
              onClick={() => setActiveTab('attendance')}
            >
              Attendance
            </button>
          )}
          <button
            className={`marsh-tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            Notifications
          </button>
        </div>

        <div className="settings-main-content">
          <div className="marsh-card-settings">
            {activeTab === 'profile' && (
              <div className="settings-section">
                <div className="section-header-row">
                  <div className="section-line"></div>
                  <h2>Profile Settings</h2>
                </div>
                <form onSubmit={handleProfileUpdate} className="marsh-form">
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="marsh-label">Full Name</label>
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        className="marsh-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Email</label>
                      <input
                        type="email"
                        value={profileData.email}
                        className="marsh-input disabled"
                        disabled
                      />
                      <span className="marsh-hint">Locked for security</span>
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Mobile</label>
                      <div className={`premium-phone-input ${phoneError ? 'has-error' : ''}`}>
                        <div className="country-select-wrapper">
                          {selectedMobileCountry ? (
                            <>
                              <img
                                src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedMobileCountry}.svg`}
                                alt={selectedMobileCountry}
                                className="flag-icon"
                              />
                              <span className="calling-code">+{getCountryCallingCode(selectedMobileCountry)}</span>
                            </>
                          ) : (
                            <div className="globe-icon">🌐</div>
                          )}
                          <select
                            className="country-native-select"
                            value={selectedMobileCountry || ''}
                            onChange={(e) => {
                              const newCountry = e.target.value || null;
                              setSelectedMobileCountry(newCountry);
                              setPhoneError('');
                            }}
                            disabled={loading}
                          >
                            <option value="">Select Country</option>
                            {getCountries().map((country) => (
                              <option key={country} value={country}>
                                {en[country]} (+{getCountryCallingCode(country)})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="phone-input-field-wrapper">
                          <Input
                            country={selectedMobileCountry}
                            placeholder={selectedMobileCountry ? `Enter number...` : "Select country"}
                            value={profileData.mobile}
                            onChange={(value) => {
                              setProfileData(prev => ({ ...prev, mobile: value || '' }));
                              if (value) {
                                if (isValidPhoneNumber(value)) {
                                  setPhoneError('');
                                } else {
                                  setPhoneError('Invalid number');
                                }
                              } else {
                                setPhoneError('');
                              }
                            }}
                            disabled={!selectedMobileCountry || loading}
                            className="PhoneInputInput"
                          />
                        </div>
                      </div>
                      {phoneError && <span className="error-text">{phoneError}</span>}
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Role</label>
                      <input
                        type="text"
                        value={profileData.role}
                        className="marsh-input disabled"
                        disabled
                      />
                      <span className="marsh-hint">Assigned role</span>
                    </div>
                  </div>

                  <button type="submit" className="marsh-btn-primary" disabled={loading}>
                    {loading ? 'Processing...' : 'Save Profile Changes'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'password' && (
              <div className="settings-section">
                <div className="section-header-row">
                  <div className="section-line"></div>
                  <h2>Security Credentials</h2>
                </div>
                <form onSubmit={handlePasswordChange} className="marsh-form">
                  <div className="form-group">
                    <label className="marsh-label">Current Password</label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      className="marsh-input"
                      required
                    />
                  </div>

                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="marsh-label">New Password</label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        className="marsh-input"
                        required
                        minLength="6"
                      />
                      <span className="marsh-hint">At least 6 characters</span>
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Confirm Password</label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        className="marsh-input"
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" className="marsh-btn-primary" disabled={loading}>
                    {loading ? 'Changing...' : 'Update Password'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'organization' && (
              <div className="settings-section">
                <div className="section-header-row">
                  <div className="section-line"></div>
                  <h2>Organization Details</h2>
                </div>
                <form onSubmit={handleOrgUpdate} className="marsh-form">
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="marsh-label">Entity Name</label>
                      <input
                        type="text"
                        value={orgData.name}
                        onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                        className="marsh-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Type</label>
                      <input
                        type="text"
                        value={orgData.type}
                        className="marsh-input disabled"
                        disabled
                      />
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Country</label>
                      <div className="marsh-select-wrapper">
                        {orgData.country && getCountries().includes(orgData.country) && (
                          <img
                            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${orgData.country}.svg`}
                            alt={orgData.country}
                            className="select-flag-marsh"
                          />
                        )}
                        <select
                          value={orgData.country}
                          onChange={(e) => {
                            const newCountry = e.target.value;
                            const newCurrency = COUNTRY_CURRENCY_MAP[newCountry];
                            setOrgData(prev => ({
                              ...prev,
                              country: newCountry,
                              baseCurrency: newCurrency || prev.baseCurrency
                            }));
                          }}
                          className="marsh-input with-flag"
                          required
                        >
                          <option value="">Select Country</option>
                          {getCountries().map((country) => (
                            <option key={country} value={country}>
                              {en[country]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Preferred Currency</label>
                      <select
                        value={orgData.baseCurrency || 'INR'}
                        onChange={(e) => setOrgData({ ...orgData, baseCurrency: e.target.value })}
                        className="marsh-input"
                        required
                      >
                        {Object.entries(CURRENCY_LABELS).sort().map(([code, label]) => (
                          <option key={code} value={code}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="marsh-label">Operational Timezone</label>
                    <select
                      value={orgData.timezone}
                      onChange={(e) => setOrgData({ ...orgData, timezone: e.target.value })}
                      className="marsh-input"
                      required
                    >
                      <option value="">Select Timezone</option>
                      <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                      <option value="America/New_York">America/New_York (EST)</option>
                      <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                      <option value="Europe/London">Europe/London (GMT)</option>
                      <option value="Europe/Paris">Europe/Paris (CET)</option>
                    </select>
                  </div>

                  <button type="submit" className="marsh-btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Update Organization'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="settings-section">
                <div className="section-header-row">
                  <div className="section-line"></div>
                  <h2>Reachability Prefs</h2>
                </div>
                <div className="marsh-toggle-list">
                  {[
                    { key: 'taskAssigned', label: 'New Task Assignment', sub: 'Alert when someone assigns work to you' },
                    { key: 'dependencyCleared', label: 'Dependency Cleared', sub: 'Alert when a blocking task is finished' },
                    { key: 'deadlineApproaching', label: 'Deadline Pushes', sub: 'Urgent alerts for upcoming deadlines' },
                    { key: 'milestoneApproved', label: 'Milestone Progress', sub: 'Confirmations for approved stages' },
                    { key: 'emailNotifications', label: 'External Email Sync', sub: 'Send these alerts to your inbox too' }
                  ].map((item) => (
                    <div className="marsh-toggle-item" key={item.key}>
                      <div className="toggle-info">
                        <h4>{item.label}</h4>
                        <p>{item.sub}</p>
                      </div>
                      <label className="marsh-switch">
                        <input
                          type="checkbox"
                          checked={notifications[item.key]}
                          onChange={() => handleNotificationToggle(item.key)}
                        />
                        <span className="marsh-slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'attendance' && user?.role === 'HR' && (
              <div className="settings-section">
                <div className="section-header-row">
                  <div className="section-line"></div>
                  <h2>Attendance Policy</h2>
                </div>
                <p className="marsh-section-desc">Global rules for punch-ins and half-day triggers</p>
                <form onSubmit={handleAttendanceSettingsUpdate} className="marsh-form">
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="marsh-label">Shift Start Time</label>
                      <input
                        type="time"
                        value={attendanceSettings.standardPunchInTime}
                        onChange={(e) => setAttendanceSettings({ ...attendanceSettings, standardPunchInTime: e.target.value })}
                        className="marsh-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Late Grace (Mins)</label>
                      <input
                        type="number"
                        value={attendanceSettings.lateThresholdMinutes}
                        onChange={(e) => setAttendanceSettings({ ...attendanceSettings, lateThresholdMinutes: parseInt(e.target.value) })}
                        className="marsh-input"
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Max Late Instances</label>
                      <input
                        type="number"
                        value={attendanceSettings.maxLateAllowed}
                        onChange={(e) => setAttendanceSettings({ ...attendanceSettings, maxLateAllowed: parseInt(e.target.value) })}
                        className="marsh-input"
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="marsh-label">Auto-Reset Cycle</label>
                      <select
                        value={attendanceSettings.resetPeriod}
                        onChange={(e) => setAttendanceSettings({ ...attendanceSettings, resetPeriod: e.target.value })}
                        className="marsh-input"
                        required
                      >
                        <option value="Monthly">Monthly</option>
                        <option value="Quarterly">Quarterly</option>
                      </select>
                    </div>
                  </div>

                  <div className="marsh-info-puffy">
                    <p><strong>Office Geography Filter</strong></p>
                    <div className="form-grid-2" style={{ marginTop: '12px' }}>
                      <div className="form-group">
                        <label className="marsh-label">Latitude</label>
                        <input
                          type="number"
                          step="any"
                          value={attendanceSettings.officeCoordinates.latitude}
                          onChange={(e) => setAttendanceSettings({
                            ...attendanceSettings,
                            officeCoordinates: { ...attendanceSettings.officeCoordinates, latitude: e.target.value }
                          })}
                          className="marsh-input"
                          placeholder="0.0000"
                        />
                      </div>
                      <div className="form-group">
                        <label className="marsh-label">Longitude</label>
                        <input
                          type="number"
                          step="any"
                          value={attendanceSettings.officeCoordinates.longitude}
                          onChange={(e) => setAttendanceSettings({
                            ...attendanceSettings,
                            officeCoordinates: { ...attendanceSettings.officeCoordinates, longitude: e.target.value }
                          })}
                          className="marsh-input"
                          placeholder="0.0000"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="marsh-btn-secondary"
                      onClick={captureOfficeLocation}
                      disabled={loading}
                      style={{ marginTop: '16px' }}
                    >
                      Use My Accurate Location
                    </button>
                  </div>

                  <button type="submit" className="marsh-btn-primary" disabled={loading}>
                    {loading ? 'Processing...' : 'Deploy Attendance Rules'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
